import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from './Layout';
import useIsMobile from '../hooks/useIsMobile';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const tkey = (t) => `${t.target_type}:${t.id}`;

export default function AddPatient() {
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  // N-115: тот же флоу, но mode=operation — направление на «Операцию» (отдельная
  // параллельная система, см. clinics.py get_bind_targets?mode=). Единственное
  // технически отличие внутри компонента: параметр mode в запросах целей/бинда и
  // другие поля условий (operation_commission_pct/operation_timeout_days вместо
  // первичного/повторного %/квоты).
  const mode = searchParams.get('mode') === 'operation' ? 'operation' : 'standard';
  const isOperation = mode === 'operation';
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [step, setStep] = useState(1); // 1-данные, 2-поиск услуги, 3-выбор целей, 4-подтверждение
  const [form, setForm] = useState({ first_name: '', last_name: '', middle_name: '', birth_date: '' });
  const [serviceSearch, setServiceSearch] = useState('');
  const [targets, setTargets] = useState([]);
  const [selected, setSelected] = useState([]); // массив ключей "type:id"
  const [alreadyBound, setAlreadyBound] = useState([]); // ключи уже привязанных целей
  const [maxPerPatient, setMaxPerPatient] = useState(10);
  const [baseLimit, setBaseLimit] = useState(100);
  const [verificationStatus, setVerificationStatus] = useState('active');
  const [verificationReason, setVerificationReason] = useState('');
  const [patientId, setPatientId] = useState(null);
  const [consent, setConsent] = useState(false); // N-063: согласие пациента на передачу ФИО+ДР клинике
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/patients/limits`, { headers }).then(r => {
      setBaseLimit(r.data.base_limit);
      setVerificationStatus(r.data.verification_status);
      setVerificationReason(r.data.verification_reject_reason || '');
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const remaining = maxPerPatient - alreadyBound.length - selected.length;

  const loadTargets = async (pid, service) => {
    const params = { mode };
    if (pid) params.patient_id = pid;
    if (service && service.trim().length >= 2) params.service = service.trim();
    const r = await axios.get(`${API}/clinics/targets`, { headers, params });
    setTargets(r.data.targets);
    setAlreadyBound(r.data.already_bound.map(tkey));
    setMaxPerPatient(r.data.max_per_patient);
  };

  const handle = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleStep1 = async () => {
    if (!form.first_name || !form.last_name || !form.middle_name || !form.birth_date) {
      setError('Заполните обязательные поля (включая отчество)');
      return;
    }
    setLoading(true);
    try {
      // N-128: только СМОТРИМ, есть ли уже такой пациент (read-only) — саму запись не
      // создаём здесь, чтобы брошенный на середине визард не оставлял в базе пациента
      // без единого направления. Реальное создание — атомарно с направлением на шаге 4.
      const res = await axios.get(`${API}/patients/lookup`, { headers, params: form });
      setPatientId(res.data.id);
      await loadTargets(res.data.id, '');
      setError('');
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || 'Ошибка при проверке пациента');
    }
    setLoading(false);
  };

  const handleServiceSearch = async (val) => {
    setServiceSearch(val);
    try { await loadTargets(patientId, val); } catch {}
  };

  const toggleTarget = (t) => {
    const k = tkey(t);
    if (selected.includes(k)) {
      setSelected(selected.filter(x => x !== k));
      setError('');
      return;
    }
    if (remaining <= 0) {
      setError(`Лимит ${maxPerPatient} целей на пациента исчерпан${alreadyBound.length ? ` (уже привязано ${alreadyBound.length})` : ''}`);
      return;
    }
    setSelected([...selected, k]);
    setError('');
  };

  const handleConfirm = async () => {
    if (selected.length === 0) {
      setError('Выберите хотя бы одну цель');
      return;
    }
    setLoading(true);
    try {
      // N-128: если пациент новый (patientId ещё не найден на шаге 1) — передаём его
      // данные, чтобы бэкенд создал запись АТОМАРНО вместе с направлением, одной транзакцией.
      const payload = {
        ...(patientId ? { patient_id: patientId } : { ...form }),
        targets: selected.map(k => { const [target_type, id] = k.split(':'); return { target_type, id: Number(id) }; }),
        consent,
        referral_type: mode,
      };
      const res = await axios.post(`${API}/patients/bind`, payload, { headers });
      if (res.data.errors.length > 0 && res.data.bound.length === 0) {
        setError(res.data.errors.map(e => e.error).join(', '));
      } else {
        navigate('/patients');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Ошибка при привязке пациента');
    }
    setLoading(false);
  };

  const selectedObjects = targets.filter(t => selected.includes(tkey(t)));

  return (
    <Layout>
      {showDisclaimer && (
        <div style={styles.overlay}>
          <div style={styles.modalSm}>
            <h3 style={styles.modalTitle}>Перед добавлением пациента</h3>
            <p style={styles.hint}>
              Добавляйте только реальных пациентов, которых вы действительно направляете. <b>Удалить направление нельзя</b> —
              оно займёт место в активной базе направлений (лимит {baseLimit}) до тех пор, пока пациент не дойдёт до оплаты,
              не будет признан дублем, или направление не сгорит по истечении срока клиники. Каждая цель — отдельное направление:
              один пациент в 2 клиниках занимает 2 места в лимите.
            </p>
            <button style={styles.btn} onClick={() => setShowDisclaimer(false)}>Понятно, продолжить</button>
          </div>
        </div>
      )}
      <div style={styles.container}>
        <div style={styles.topRow}>
          <h1 style={styles.h1}>
            {isOperation ? 'Новое направление на операцию' : 'Новый пациент'}
            {isOperation && <span style={styles.tagOperation}>Операция</span>}
          </h1>
          <button style={styles.btnBack} onClick={() => navigate('/patients')}>← К пациентам</button>
        </div>
        {verificationStatus !== 'active' ? (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Добавление пациентов недоступно</h2>
            <p style={styles.hint}>
              {verificationStatus === 'pending'
                ? 'Ваша выписка ЕГИСЗ на проверке у администратора. Как только подтвердим — сможете добавлять пациентов.'
                : verificationStatus === 'rejected'
                  ? `Верификация отклонена${verificationReason ? ': ' + verificationReason : ''}. Загрузите документ повторно в профиле.`
                  : 'Чтобы добавлять пациентов, загрузите выписку ЕГИСЗ об аккредитации в профиле.'}
            </p>
            <button style={styles.btn} onClick={() => navigate('/profile')}>Перейти в профиль</button>
          </div>
        ) : (
        <>
        <div style={{ ...styles.steps, ...(isMobile ? styles.stepsMobile : {}) }}>
          {['Данные пациента', 'Поиск услуги', 'Выбор целей', 'Подтверждение'].map((s, i) => (
            <div key={i} style={styles.stepItem}>
              <div style={{...styles.stepCircle, ...(step > i + 1 ? styles.stepDone : step === i + 1 ? styles.stepActive : {})}}>
                {step > i + 1 ? '✓' : i + 1}
              </div>
              <span style={{...styles.stepLabel, ...(step === i + 1 ? styles.stepLabelActive : {})}}>{s}</span>
            </div>
          ))}
        </div>

        <div style={styles.card}>
          {step === 1 && (
            <>
              <h2 style={styles.cardTitle}>Данные пациента</h2>
              <div style={{ ...styles.row, ...(isMobile ? styles.rowMobile : {}) }}>
                <input style={styles.input} placeholder="Фамилия *" value={form.last_name} onChange={handle('last_name')} />
                <input style={styles.input} placeholder="Имя *" value={form.first_name} onChange={handle('first_name')} />
              </div>
              <input style={styles.inputFull} placeholder="Отчество *" value={form.middle_name} onChange={handle('middle_name')} />
              <label style={styles.label}>Дата рождения *</label>
              <input style={styles.inputFull} type="date" value={form.birth_date} onChange={handle('birth_date')} />
              {error && <div style={styles.error}>{error}</div>}
              <button style={styles.btn} onClick={handleStep1} disabled={loading}>
                {loading ? 'Сохранение...' : 'Далее →'}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <h2 style={styles.cardTitle}>Поиск услуги</h2>
              <p style={styles.hint}>Введите название услуги, чтобы найти клиники и сети, где она есть. Или пропустите, чтобы увидеть все цели.</p>
              <input
                style={styles.inputFull}
                placeholder="Например: КТ головного мозга..."
                value={serviceSearch}
                onChange={e => handleServiceSearch(e.target.value)}
                autoFocus
              />
              {serviceSearch && <p style={styles.hint}>Найдено целей с такой услугой: {targets.length}</p>}
              <div style={styles.btnRow}>
                <button style={styles.btnSecondary} onClick={async () => { setServiceSearch(''); await loadTargets(patientId, ''); setStep(3); }}>
                  Пропустить →
                </button>
                <button
                  style={{...styles.btn, ...(targets.length === 0 ? styles.btnDisabled : {})}}
                  onClick={() => setStep(3)}
                  disabled={targets.length === 0}
                >
                  Далее → ({targets.length})
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2 style={styles.cardTitle}>Выберите цели</h2>
              <p style={styles.hint}>
                Выбрано: {selected.length}{alreadyBound.length ? ` (+${alreadyBound.length} уже привязано)` : ''} · осталось {Math.max(remaining, 0)} из {maxPerPatient}
              </p>
              {targets.length === 0 ? (
                <div style={styles.empty}>Цели не найдены</div>
              ) : (
                <div style={styles.clinicList}>
                  {targets.map(t => {
                    const k = tkey(t);
                    const isSel = selected.includes(k);
                    return (
                      <div
                        key={k}
                        style={{...styles.clinicItem, ...(isSel ? styles.clinicItemSelected : {})}}
                        onClick={() => toggleTarget(t)}
                      >
                        <div style={styles.clinicCheck}>{isSel ? '☑' : '☐'}</div>
                        <div style={styles.clinicInfo}>
                          <div style={styles.clinicName}>
                            {t.name}
                            <span style={t.target_type === 'network' ? styles.tagNet : styles.tagClinic}>
                              {t.target_type === 'network' ? `сеть · ${t.member_count} учр.` : 'клиника'}
                            </span>
                          </div>
                          <div style={styles.clinicMeta}>
                            {isOperation
                              ? (t.operation_commission_pct != null
                                  ? `Комиссия: ${t.operation_commission_pct}% от суммы всех оплат · Период: ${t.operation_timeout_days} дн.`
                                  : 'Условия ещё не заданы')
                              : (t.commission_primary_pct != null
                                  ? `Первичный: ${t.commission_primary_pct}% · Повторный: ${t.commission_repeat_pct}% · Квота: ${t.monthly_quota}/мес · Таймер: ${t.timeout_days} дн.`
                                  : 'Условия ещё не заданы')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {error && <div style={styles.error}>{error}</div>}
              <div style={styles.btnRow}>
                <button style={styles.btnSecondary} onClick={() => setStep(2)}>← Назад</button>
                <button style={styles.btn} onClick={() => setStep(4)} disabled={selected.length === 0}>
                  Далее →
                </button>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <h2 style={styles.cardTitle}>Подтверждение</h2>
              <div style={styles.confirmBlock}>
                <div style={styles.confirmLabel}>Пациент</div>
                <div style={styles.confirmValue}>{form.last_name} {form.first_name} {form.middle_name}</div>
                <div style={styles.confirmValue}>{new Date(form.birth_date).toLocaleDateString('ru')}</div>
              </div>
              <div style={styles.confirmBlock}>
                <div style={styles.confirmLabel}>Цели ({selectedObjects.length})</div>
                {selectedObjects.map(t => (
                  <div key={tkey(t)} style={styles.confirmClinic}>
                    <span>
                      {t.name}
                      <span style={t.target_type === 'network' ? styles.tagNet : styles.tagClinic}>
                        {t.target_type === 'network' ? 'сеть' : 'клиника'}
                      </span>
                    </span>
                    {isOperation ? (
                      t.operation_commission_pct != null && (
                        <span style={styles.confirmPct}>{t.operation_commission_pct}% · {t.operation_timeout_days} дн.</span>
                      )
                    ) : (
                      t.commission_primary_pct != null && (
                        <span style={styles.confirmPct}>
                          <span title="Первичный визит">1й: {t.commission_primary_pct}%</span>
                          <span style={{margin: '0 4px', color: '#ccc'}}>·</span>
                          <span title="Повторные визиты">2й+: {t.commission_repeat_pct}%</span>
                        </span>
                      )
                    )}
                  </div>
                ))}
              </div>
              {error && <div style={styles.error}>{error}</div>}
              <div style={styles.confirmFooterRow}>
                <label style={styles.consentLabel}>
                  <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} style={styles.consentCheckbox} />
                  Согласие пациента на предоставление ФИО и даты рождения клинике получено
                </label>
                <div style={styles.btnRow}>
                  <button style={styles.btnSecondary} onClick={() => setStep(3)}>← Назад</button>
                  <button style={styles.btn} onClick={handleConfirm} disabled={loading || !consent}>
                    {loading ? 'Сохранение...' : '✓ Подтвердить'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        </>
        )}
      </div>
    </Layout>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modalSm: { background: '#fff', borderRadius: 20, padding: 28, width: 480, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 20px 60px rgba(15,23,42,0.3)' },
  modalTitle: { fontSize: 18, fontWeight: 700, color: '#1B2B4B', margin: 0 },
  container: { maxWidth: 640, margin: '0 auto' },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  h1: { fontSize: 28, fontWeight: 800, color: '#1B2B4B', margin: 0, display: 'flex', alignItems: 'center', gap: 10 },
  tagOperation: { fontSize: 12, fontWeight: 700, color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d', padding: '3px 10px', borderRadius: 20 },
  btnBack: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', padding: '9px 16px', borderRadius: 10, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0 },
  steps: { display: 'flex', justifyContent: 'space-between', marginBottom: 24 },
  stepsMobile: { gap: 4 },
  stepItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 },
  stepCircle: { width: 32, height: 32, borderRadius: 16, background: '#eef2f7', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 },
  stepActive: { background: '#2EC4B6', color: 'white' },
  stepDone: { background: '#149e90', color: 'white' },
  stepLabel: { fontSize: 11, color: '#94a3b8', textAlign: 'center' },
  stepLabelActive: { color: '#1B2B4B', fontWeight: 600 },
  card: { background: 'white', borderRadius: 16, padding: 28, boxShadow: '0 4px 16px rgba(27,43,75,0.06)', display: 'flex', flexDirection: 'column', gap: 16 },
  cardTitle: { fontSize: 18, fontWeight: 700, color: '#1B2B4B', margin: 0 },
  hint: { fontSize: 13, color: '#64748b', margin: 0 },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  rowMobile: { gridTemplateColumns: '1fr' },
  input: { padding: '12px 14px', border: '1px solid #d7dee8', borderRadius: 10, fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  inputFull: { padding: '12px 14px', border: '1px solid #d7dee8', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  label: { fontSize: 12, color: '#64748b', marginBottom: -8 },
  btn: { background: '#2EC4B6', color: 'white', border: 'none', padding: '12px 18px', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnSecondary: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', padding: '12px 18px', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnRow: { display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' },
  confirmFooterRow: { display: 'flex', gap: 16, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' },
  consentLabel: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569', cursor: 'pointer', maxWidth: 320 },
  consentCheckbox: { width: 16, height: 16, flexShrink: 0, accentColor: '#2EC4B6', cursor: 'pointer' },
  error: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13 },
  clinicList: { display: 'flex', flexDirection: 'column', gap: 8 },
  clinicItem: { display: 'flex', gap: 12, padding: '14px 16px', border: '1px solid #eef2f7', borderRadius: 12, cursor: 'pointer', alignItems: 'center' },
  clinicItemSelected: { border: '2px solid #2EC4B6', background: '#f0fffe' },
  clinicCheck: { fontSize: 20, color: '#2EC4B6', flexShrink: 0 },
  clinicInfo: {},
  clinicName: { fontSize: 15, fontWeight: 600, color: '#1B2B4B', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  clinicMeta: { fontSize: 12, color: '#64748b' },
  tagNet: { fontSize: 11, fontWeight: 600, color: '#7c3aed', background: '#f3e8ff', padding: '2px 8px', borderRadius: 20 },
  tagClinic: { fontSize: 11, fontWeight: 600, color: '#149e90', background: '#e9faf7', padding: '2px 8px', borderRadius: 20 },
  empty: { textAlign: 'center', color: '#94a3b8', padding: 32 },
  confirmBlock: { background: '#f6f8fb', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 6 },
  confirmLabel: { fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  confirmValue: { fontSize: 16, fontWeight: 600, color: '#1B2B4B' },
  confirmClinic: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, color: '#333', gap: 12 },
  confirmPct: { color: '#149e90', fontWeight: 600, whiteSpace: 'nowrap' },
  btnDisabled: { background: '#a0d8d4', cursor: 'not-allowed' },
};
